import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "Lido Blu",
        metaTitle: "Lido Blu | Locale sulla spiaggia",
        metaDescription: "Aperitivi al tramonto, cucina fresca e serate con musica. Prenota il tuo tavolo sul mare.",
        intro: "Aperitivo al tramonto, cucina di mare e serate live. Un posto semplice e bello, direttamente sulla sabbia.",
        menu: [
            { label: "Menù", href: "#menu" },
            { label: "Serata", href: "#serata" },
            { label: "Prenota", href: "#prenota" }
        ],
        ctaPrimary: "Prenota un tavolo",
        ctaSecondary: "Guarda il menù",
        hoursTitle: "Orari estivi",
        hoursEveryday: "Tutti i giorni",
        hoursTime: "18:00 – 02:00",
        hoursNote: "Aperitivo vista mare dalle 19:00",
        menuTitle: "Menù estivo",
        menuSubtitle: "Cucina fresca e leggera, perfetta per l’estate.",
        menuItems: [
            { name: "Crudi di mare", price: "€18" },
            { name: "Pasta alla bottarga", price: "€16" },
            { name: "Cocktail Lido", price: "€10" }
        ],
        nightTitle: "Stasera live",
        nightSubtitle: "DJ set e sax al tramonto",
        nightStartLabel: "Inizio",
        nightStartTime: "21:30",
        nightNote: "Ingresso libero, prenotazione consigliata.",
        bookTitle: "Prenota il tuo tavolo",
        bookSubtitle: "Risposta veloce su WhatsApp o telefono.",
        bookCta1: "Prenota su WhatsApp",
        bookCta2: "Chiama ora"
    },
    en: {
        title: "Lido Blu",
        metaTitle: "Lido Blu | Beach lounge",
        metaDescription: "Sunset aperitif, fresh seafood and live nights. Book your table by the sea.",
        intro: "Sunset aperitif, seafood dishes and live nights. A simple, beautiful place right on the sand.",
        menu: [
            { label: "Menu", href: "#menu" },
            { label: "Tonight", href: "#serata" },
            { label: "Book", href: "#prenota" }
        ],
        ctaPrimary: "Book a table",
        ctaSecondary: "See the menu",
        hoursTitle: "Summer hours",
        hoursEveryday: "Every day",
        hoursTime: "6:00 pm – 2:00 am",
        hoursNote: "Sunset aperitif from 7:00 pm",
        menuTitle: "Summer menu",
        menuSubtitle: "Fresh and light, perfect for summer.",
        menuItems: [
            { name: "Seafood crudo", price: "€18" },
            { name: "Bottarga pasta", price: "€16" },
            { name: "Lido cocktail", price: "€10" }
        ],
        nightTitle: "Live tonight",
        nightSubtitle: "DJ set and sax at sunset",
        nightStartLabel: "Start",
        nightStartTime: "21:30",
        nightNote: "Free entry, booking recommended.",
        bookTitle: "Book your table",
        bookSubtitle: "Quick reply on WhatsApp or phone.",
        bookCta1: "Book on WhatsApp",
        bookCta2: "Call now"
    },
    de: {
        title: "Lido Blu",
        metaTitle: "Lido Blu | Strandbar",
        metaDescription: "Aperitif zum Sonnenuntergang, frische Küche und Live‑Abende. Tisch reservieren.",
        intro: "Aperitif zum Sonnenuntergang, Meeresküche und Live‑Abende. Ein schöner Ort direkt im Sand.",
        menu: [
            { label: "Menü", href: "#menu" },
            { label: "Heute", href: "#serata" },
            { label: "Buchen", href: "#prenota" }
        ],
        ctaPrimary: "Tisch reservieren",
        ctaSecondary: "Menü ansehen",
        hoursTitle: "Sommerzeiten",
        hoursEveryday: "Täglich",
        hoursTime: "18:00 – 02:00",
        hoursNote: "Aperitif zum Sonnenuntergang ab 19:00",
        menuTitle: "Sommermenü",
        menuSubtitle: "Frisch und leicht, perfekt für den Sommer.",
        menuItems: [
            { name: "Meeresfrüchte roh", price: "€18" },
            { name: "Bottarga‑Pasta", price: "€16" },
            { name: "Lido‑Cocktail", price: "€10" }
        ],
        nightTitle: "Heute live",
        nightSubtitle: "DJ‑Set und Saxophon bei Sonnenuntergang",
        nightStartLabel: "Start",
        nightStartTime: "21:30",
        nightNote: "Eintritt frei, Reservierung empfohlen.",
        bookTitle: "Tisch reservieren",
        bookSubtitle: "Schnelle Antwort per WhatsApp oder Telefon.",
        bookCta1: "Per WhatsApp buchen",
        bookCta2: "Jetzt anrufen"
    }
};

const LocaleSpiaggiaPage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/locale-spiaggia"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-gradient-to-b from-cyan-50 via-white to-amber-50 text-slate-900">
                <header className="relative px-6 pt-10 pb-10 md:px-12 lg:px-20">
                    <div className="absolute -top-12 right-8 h-40 w-40 rounded-full bg-cyan-200/60 blur-3xl"></div>
                    <ExampleLandingHeader title={t.title} items={t.menu} />
                    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_0.8fr]">
                        <div>
                            <p className="text-lg text-slate-700">{t.intro}</p>
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a href="#prenota" className="rounded-full bg-cyan-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-cyan-200">
                                    {t.ctaPrimary}
                                </a>
                                <a href="#menu" className="rounded-full border border-cyan-600 px-6 py-3 text-sm font-semibold text-cyan-700">
                                    {t.ctaSecondary}
                                </a>
                            </div>
                        </div>
                        <div className="rounded-3xl bg-white p-6 shadow-lg">
                            <h2 className="text-xl font-semibold">{t.hoursTitle}</h2>
                            <p className="mt-2 text-sm text-slate-600">{t.hoursEveryday}</p>
                            <p className="mt-4 text-3xl font-semibold">{t.hoursTime}</p>
                            <p className="mt-4 text-sm text-slate-600">{t.hoursNote}</p>
                        </div>
                    </div>
                    <div className="mt-8 overflow-hidden rounded-3xl shadow-lg">
                        <img
                            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1600&auto=format&fit=crop"
                            alt="Locale sulla spiaggia al tramonto"
                            className="h-64 w-full object-cover md:h-96"
                            loading="lazy"
                        />
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="menu" className="grid gap-6 lg:grid-cols-[1fr_1fr]">
                        <div className="rounded-3xl bg-white p-6 shadow-lg md:p-10">
                            <h2 className="text-2xl font-semibold">{t.menuTitle}</h2>
                            <p className="mt-2 text-slate-600">{t.menuSubtitle}</p>
                            <div className="mt-6 space-y-4">
                                {t.menuItems.map((item) => (
                                    <div key={item.name} className="flex items-center justify-between border-b border-cyan-100 pb-2">
                                        <p className="font-semibold">{item.name}</p>
                                        <p className="text-sm text-slate-500">{item.price}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div id="serata" className="rounded-3xl bg-cyan-700 p-6 text-white shadow-lg md:p-10">
                            <h2 className="text-2xl font-semibold">{t.nightTitle}</h2>
                            <p className="mt-2 text-cyan-100">{t.nightSubtitle}</p>
                            <div className="mt-6 rounded-2xl bg-cyan-800 p-4">
                                <p className="text-sm uppercase tracking-widest">{t.nightStartLabel}</p>
                                <p className="mt-2 text-3xl font-semibold">{t.nightStartTime}</p>
                            </div>
                            <p className="mt-6 text-sm text-cyan-100">{t.nightNote}</p>
                        </div>
                    </section>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <img
                            src="https://images.unsplash.com/photo-1501973801540-537f08ccae7b?q=80&w=1400&auto=format&fit=crop"
                            alt="Cocktail al tramonto"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1400&auto=format&fit=crop"
                            alt="Tavolo sulla spiaggia"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                    </div>

                    <section id="prenota" className="mt-10 rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.bookTitle}</h2>
                        <p className="mt-2 text-slate-600">{t.bookSubtitle}</p>
                        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                            <a href="https://wa.me/393515699197" className="rounded-full bg-cyan-600 px-6 py-3 text-sm font-semibold text-white">
                                {t.bookCta1}
                            </a>
                            <a href="tel:+393515699197" className="rounded-full border border-cyan-600 px-6 py-3 text-sm font-semibold text-cyan-700">
                                {t.bookCta2}
                            </a>
                        </div>
                    </section>
                    <SocialLinksSection className="mt-10" />
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default LocaleSpiaggiaPage;
